import { useState } from 'react';
import logoIcon from '../../assets/images/standalone_logo.png';
import logoFull from '../../assets/images/logo_aktivity.png';

/**
 * Word Cloud Activity Component
 * Clean UI design with word input and circular timer
 */

const WordCloudActivity = ({
  activity,
  sessionCode,
  sessionName = 'Live Session',
  trainerName = 'Trainer',
  timeRemaining,
  onSubmit,
  showResults = false,
  userWord = '',
  onBackToList,
  isActivityEnded = false,
}) => {
  const [wordInput, setWordInput] = useState('');
  const [submittedWords, setSubmittedWords] = useState(userWord ? [userWord] : []);

  // Check if multiple responses are allowed
  const allowMultipleResponses = activity.config?.allowMultipleResponses || activity.allowMultipleResponses || false;

  // Use the first submitted word for backwards compatibility
  const submittedWord = submittedWords.length > 0 ? submittedWords[submittedWords.length - 1] : '';

  // For multiple responses, show results view only if explicitly set or activity ended
  // For single response, show results after any submission
  const shouldShowResults = allowMultipleResponses
    ? (showResults && isActivityEnded) || (submittedWords.length > 0 && isActivityEnded)
    : showResults || submittedWords.length > 0;

  // Format time as M:SS
  const formatTime = (seconds) => {
    if (seconds === undefined || seconds === null || isNaN(seconds)) {
      return '0:00';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Calculate timer progress - use actual timeLimit from activity
  const maxTime = activity?.timerEnabled === true
    ? (activity.timeLimit || activity.config?.timeLimit || 0)
    : 0;
  const timerEnabled = maxTime > 0;

  const handleSubmit = () => {
    if (wordInput.trim().length >= 2) {
      const newWord = wordInput.trim();
      setSubmittedWords(prev => [...prev, newWord]);
      onSubmit({ word: newWord, words: [...submittedWords, newWord] });
      setWordInput(''); // Clear input for next submission
    }
  };

  // Color classes for word cloud visualization
  const colorClasses = [
    'text-primary-600', 'text-blue-600', 'text-green-600', 'text-purple-600',
    'text-pink-600', 'text-indigo-600', 'text-red-500', 'text-teal-600',
    'text-amber-600', 'text-cyan-600'
  ];

  // Process word cloud data from activity - use real submitted words
  // Use arrays with length > 0, as empty arrays are truthy but shouldn't be used
  const rawWords = (activity.wordCloud?.length ? activity.wordCloud : null) ||
                   (activity.words?.length ? activity.words : null) ||
                   (activity.responses?.length ? activity.responses : null) ||
                   [];

  // First, aggregate duplicate words (case-insensitive) and sum their counts
  const wordCountMap = new Map();
  rawWords.forEach((item) => {
    const word = typeof item === 'string' ? item : (item.word || item.text || item.response);
    if (!word) return;

    const wordLower = word.toLowerCase().trim();
    const existingCount = typeof item === 'object' ? (item.count || item.votes || 1) : 1;

    if (wordCountMap.has(wordLower)) {
      const existing = wordCountMap.get(wordLower);
      existing.count += existingCount;
    } else {
      // Preserve original casing from first occurrence
      wordCountMap.set(wordLower, {
        word: word.trim(),
        count: existingCount
      });
    }
  });

  // Convert aggregated map to array with size and color
  const aggregatedWords = Array.from(wordCountMap.values());
  const maxCount = Math.max(...aggregatedWords.map(w => w.count), 1);

  let wordCloudData = aggregatedWords.map((item, index) => {
    // Calculate size based on count (1-4 scale)
    const size = Math.min(4, Math.max(1, Math.ceil((item.count / maxCount) * 4)));
    return {
      word: item.word,
      size,
      count: item.count,
      color: colorClasses[index % colorClasses.length]
    };
  });

  // If showing results and user submitted a word, make sure it's included in the word cloud
  if (showResults && submittedWord && submittedWord.trim()) {
    const userWordLower = submittedWord.trim().toLowerCase();
    const existingWord = wordCloudData.find(w => w.word.toLowerCase() === userWordLower);
    if (!existingWord) {
      // Add user's word if not already present
      wordCloudData = [...wordCloudData, {
        word: submittedWord.trim(),
        size: 1,
        count: 1,
        color: colorClasses[wordCloudData.length % colorClasses.length]
      }];
    }
  }

  // Get top words sorted by count
  const topWords = [...wordCloudData]
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)
    .map(item => ({
      word: item.word,
      count: item.count,
      isUser: submittedWord && item.word.toLowerCase() === submittedWord.toLowerCase()
    }));

  // Activity Ended but Not Responded View
  if (isActivityEnded && !shouldShowResults && submittedWords.length === 0) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                  <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                  <span className="text-xs font-medium text-neutral-600">Ended</span>
                </div>
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">WORD CLOUD</h2>
          </div>
        </div>

        {/* Not Attended Message */}
        <div className="max-w-4xl mx-auto px-6 py-8">
          <div className="bg-neutral-100 border border-neutral-300 rounded-xl p-8 text-center">
            <div className="w-16 h-16 bg-neutral-400 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-neutral-700 mb-2">Activity Ended</h2>
            <p className="text-neutral-600 mb-4">You didn't attend this word cloud activity.</p>
            <p className="text-sm text-neutral-500">This activity has already ended, so you can no longer submit a response.</p>
            {onBackToList && (
              <button
                onClick={onBackToList}
                className="mt-6 px-6 py-2 bg-neutral-600 hover:bg-neutral-700 text-white font-semibold rounded-lg transition-colors"
              >
                Back to Activities
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View (for single response or when activity ended with multiple responses)
  if (shouldShowResults) {
    return (
      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-6xl mx-auto px-6 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {onBackToList && (
                  <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                )}
                <div className="flex items-center">
                  <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                  <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isActivityEnded ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-neutral-100 border border-neutral-300 rounded-full">
                    <div className="w-1.5 h-1.5 bg-neutral-500 rounded-full"></div>
                    <span className="text-xs font-medium text-neutral-600">Ended</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                    <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-xs font-medium text-green-700">Live</span>
                  </div>
                )}
                {sessionCode && (
                  <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                    <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Activity Type Heading */}
        <div className="bg-primary-50 px-6 py-3">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-lg font-bold text-primary-600 tracking-wide">WORD CLOUD</h2>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
          {/* Success Message */}
          <div className="mb-6 sm:mb-8 p-4 sm:p-6 bg-green-50 border border-green-200 rounded-xl">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="min-w-0">
                <h2 className="text-lg sm:text-xl font-bold text-green-800">
                  {submittedWords.length > 1 ? `${submittedWords.length} Words Submitted!` : 'Word Submitted!'}
                </h2>
                <p className="text-sm sm:text-base text-green-700">
                  {isActivityEnded
                    ? `This activity has ended. Your ${submittedWords.length > 1 ? 'words have' : 'word has'} been recorded.`
                    : `Your ${submittedWords.length > 1 ? 'words have' : 'word has'} been added to the cloud`}
                </p>
              </div>
            </div>
          </div>

          {/* Your Submitted Words */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-6 mb-6">
            <h4 className="text-sm font-semibold text-neutral-700 mb-2">
              Your {submittedWords.length > 1 ? 'Responses' : 'Response'}
            </h4>
            {submittedWords.length > 1 ? (
              <div className="flex flex-wrap gap-2">
                {submittedWords.map((word, index) => (
                  <div key={index} className="px-4 py-2 bg-primary-50 border border-primary-200 rounded-lg">
                    <span className="text-lg font-bold text-primary-700">"{word}"</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 sm:p-4 bg-primary-50 border border-primary-200 rounded-xl">
                <p className="text-lg sm:text-xl font-bold text-primary-700 text-center">"{submittedWord}"</p>
              </div>
            )}
          </div>

          {/* Word Cloud Display */}
          <div className="bg-white rounded-xl border border-neutral-200 p-4 sm:p-8 mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-neutral-900 mb-4 sm:mb-6 text-center">{activity.title}</h3>

            {/* Word Cloud Visualization */}
            <div className="p-4 sm:p-8 bg-gradient-to-br from-primary-50 to-orange-50 rounded-xl mb-6 overflow-hidden">
              {wordCloudData.length > 0 ? (
                <div className="flex flex-wrap items-center justify-center gap-3 overflow-hidden">
                  {wordCloudData.map((item, index) => {
                    const sizeClasses = ['text-sm', 'text-lg', 'text-xl sm:text-2xl', 'text-2xl sm:text-3xl', 'text-3xl sm:text-4xl'];
                    const isUserWord = submittedWord && item.word.toLowerCase() === submittedWord.toLowerCase();
                    return (
                      <span
                        key={index}
                        className={`inline-block ${sizeClasses[item.size]} font-bold ${item.color} ${
                          isUserWord ? 'bg-primary-100 px-2 py-1 rounded-lg' : ''
                        } transition-transform hover:scale-110 break-all max-w-full`}
                        style={{ wordBreak: 'break-word' }}
                      >
                        {item.word}
                      </span>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center text-neutral-500 py-8">
                  <p>No words submitted yet</p>
                </div>
              )}
            </div>

            {/* Top Words List */}
            {topWords.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-semibold text-neutral-900 flex items-center gap-2">
                  <svg className="w-5 h-5 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Top Words
                </h4>
                {topWords.map((item, index) => (
                  <div
                    key={index}
                    className={`flex items-center gap-3 p-3 rounded-lg ${
                      item.isUser ? 'bg-primary-50 border border-primary-200' : 'bg-neutral-50'
                    }`}
                  >
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                      index === 0 ? 'bg-yellow-400 text-yellow-900' :
                      index === 1 ? 'bg-neutral-300 text-neutral-700' :
                      index === 2 ? 'bg-amber-600 text-white' :
                      'bg-neutral-200 text-neutral-600'
                    }`}>
                      {index + 1}
                    </span>
                    <span className="flex-1 font-medium text-neutral-900">{item.word}</span>
                    <span className="text-sm text-neutral-500">{item.count} {item.count === 1 ? 'vote' : 'votes'}</span>
                    {item.isUser && (
                      <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                        Your word
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Participation Stats */}
          {(() => {
            // Calculate actual response count from words data (sum of all word counts)
            const actualResponseCount = wordCloudData.reduce((sum, w) => sum + (w.count || 1), 0);
            // Use backend responseCount if available, otherwise use calculated count
            const displayCount = activity.responseCount > 0 ? activity.responseCount : actualResponseCount;
            const totalParticipants = activity.totalParticipants || displayCount || 1;
            const percentage = totalParticipants > 0 ? Math.round((displayCount / totalParticipants) * 100) : 100;

            return (displayCount > 0 || activity.totalParticipants > 0) && (
              <div className="p-4 bg-neutral-50 rounded-xl">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-neutral-600">
                    {displayCount} of {totalParticipants} participants submitted
                  </span>
                  <span className="font-semibold text-primary-600">
                    {percentage}%
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    );
  }

  // Input View - Clean Design
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {onBackToList && (
                <button onClick={onBackToList} className="text-neutral-600 hover:text-neutral-900 transition-colors">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
              )}
              <div className="flex items-center">
                <img src={logoIcon} alt="Logo" className="h-8 w-8 object-contain" />
                <img src={logoFull} alt="Aktivity" className="h-8 object-contain" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-50 border border-green-200 rounded-full">
                <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-xs font-medium text-green-700">Live</span>
              </div>
              {sessionCode && (
                <div className="px-2.5 py-1 bg-orange-100 rounded-md">
                  <span className="text-sm font-bold text-orange-700">#{sessionCode}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Activity Type Heading */}
      <div className="bg-primary-50 px-6 py-3">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-lg font-bold text-primary-600 tracking-wide">WORD CLOUD</h2>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 lg:px-6 py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Timer Section - Mobile (shown at top on mobile) */}
          {timerEnabled && (
          <div className="lg:hidden order-first">
            <div className="flex flex-row items-center gap-4 mb-6 p-4 bg-neutral-50 rounded-xl">
              <div className="relative w-20 h-20">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                  <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                  <circle
                    cx="48"
                    cy="48"
                    r="40"
                    stroke="#ff8a00"
                    strokeWidth="6"
                    fill="none"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 40}`}
                    strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-lg font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                </div>
              </div>
              <div className="flex-1">
                <span className="text-sm text-neutral-500">Time Remaining</span>
                {(() => {
                  // Calculate actual response count from words data
                  const actualResponseCount = wordCloudData.reduce((sum, w) => sum + (w.count || 1), 0);
                  const displayCount = activity.responseCount > 0 ? activity.responseCount : actualResponseCount;
                  const totalParticipants = activity.totalParticipants || displayCount || 0;
                  return (displayCount > 0 || totalParticipants > 0) && (
                    <div className="text-xs text-neutral-400 mt-1">
                      {displayCount}/{totalParticipants} submitted
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
          )}

          {/* Left Side - Word Input */}
          <div className="flex-1 order-2 lg:order-1">
            {/* Question Title */}
            <h1 className="text-xl lg:text-3xl font-bold text-neutral-900 mb-3 lg:mb-4 leading-tight">
              {activity.title}
            </h1>

            {activity.question && (
              <p className="text-base lg:text-lg text-neutral-600 mb-6 lg:mb-8">{activity.question}</p>
            )}

            {/* Previously Submitted Words - for multiple responses */}
            {allowMultipleResponses && submittedWords.length > 0 && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <h4 className="text-sm font-semibold text-green-800">Your Submitted Words ({submittedWords.length})</h4>
                </div>
                <div className="flex flex-wrap gap-2">
                  {submittedWords.map((word, index) => (
                    <span key={index} className="px-3 py-1 bg-white border border-green-300 rounded-full text-sm font-medium text-green-700">
                      {word}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Word Input Card */}
            <div className="bg-neutral-50 rounded-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                </div>
                <div>
                  <h3 className="font-semibold text-neutral-900">
                    {allowMultipleResponses && submittedWords.length > 0 ? 'Add Another Word' : 'Enter Your Word'}
                  </h3>
                  <p className="text-sm text-neutral-500">
                    {allowMultipleResponses
                      ? 'You can submit multiple words for this activity'
                      : 'One word that best describes the topic'}
                  </p>
                </div>
              </div>

              <div className="relative">
                <input
                  type="text"
                  value={wordInput}
                  onChange={(e) => setWordInput(e.target.value.replace(/\s/g, ''))}
                  placeholder="Type a single word..."
                  maxLength={20}
                  className="w-full px-4 py-4 text-xl font-medium text-neutral-900 bg-white border-2 border-neutral-200 rounded-xl focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100 transition-all"
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-neutral-400">
                  {wordInput.length}/20
                </span>
              </div>

              <p className="text-xs text-neutral-500 mt-2">Must be 2-20 characters, no spaces</p>
            </div>

            {/* Submit Button */}
            <div className="mb-8">
              <button
                onClick={handleSubmit}
                disabled={wordInput.trim().length < 2}
                className="px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors"
              >
                {allowMultipleResponses && submittedWords.length > 0 ? 'Add Word' : 'Submit Word'}
              </button>
            </div>

            {/* Popular Words Preview - only show if there are submitted words */}
            {topWords.length > 0 && (
              <div className="bg-neutral-50 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">Popular words so far:</h4>
                <div className="flex flex-wrap gap-2">
                  {topWords.slice(0, 5).map((item, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-white border border-neutral-200 rounded-full text-sm text-neutral-700"
                    >
                      {item.word}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right Side - Timer & Info (Desktop only) */}
          <div className="hidden lg:block w-64 flex-shrink-0 order-3 lg:order-2">
            <div className="sticky top-8">
              {/* Circular Timer */}
              {timerEnabled && (
              <div className="flex flex-col items-center mb-8">
                <div className="relative w-24 h-24">
                  <svg className="w-full h-full transform -rotate-90" viewBox="0 0 96 96">
                    <circle cx="48" cy="48" r="40" stroke="#e5e7eb" strokeWidth="6" fill="none" />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="#ff8a00"
                      strokeWidth="6"
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={`${2 * Math.PI * 40}`}
                      strokeDashoffset={`${2 * Math.PI * 40 * (1 - (timeRemaining / maxTime))}`}
                      className="transition-all duration-1000"
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-2xl font-bold text-primary-600">{formatTime(timeRemaining)}</span>
                  </div>
                </div>
                <span className="text-sm text-neutral-500 mt-2">Time Remaining</span>
              </div>
              )}

              {/* Activity Info */}
              <div className="bg-neutral-50 rounded-xl p-4">
                <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Activity Info
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Session:</span>
                    <span className="font-medium text-neutral-700">{sessionCode}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-500">Type:</span>
                    <span className="font-medium text-neutral-700">Word Cloud</span>
                  </div>
                </div>
              </div>

              {/* Tips */}
              <div className="mt-4 p-4 bg-primary-50 rounded-xl border border-primary-100">
                <div className="flex gap-2">
                  <svg className="w-5 h-5 text-primary-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <div>
                    <p className="text-sm font-medium text-primary-800">Tip</p>
                    <p className="text-xs text-primary-700">Choose a meaningful word that captures your thoughts</p>
                  </div>
                </div>
              </div>

              {/* Anonymous Notice */}
              <div className="mt-4 flex items-center gap-2 text-sm text-neutral-500">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Responses are anonymous</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WordCloudActivity;
